import { Map as OLMap } from 'ol';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserType } from '../types/User';
import { OrganisationType } from '../types/Organisation';
import { ApplicationAddressSystemType } from '../types/ApplicationAddressSystem';
import { Role } from '../types/Role';
import { RIMSPolyline } from '../types/Polylines';
import DrawingModes from '../types/Drawing';
import { Location } from '../types/Location';
import { Point } from '../types/Point';
import { Polygon } from '../types/Polygon';
import { AddressSystem } from '../types/UserRole';
import { GeometryStatus, BaseGeometry } from '../types/BaseGeometry';
import { GeometrySearchFilters, GeometrySearchResult } from '../types/GeometrySearch';
import { TreeNodeData } from '@mantine/core/lib/components/Tree/Tree';

type TransientStoreType = {

	// Client system settings
	clientSystemSettings: Map<string, string>;
	setClientSystemSettings: (settings: Map<string, string>) => void;
	
	// Helper methods for accessing settings
	getSettingValue: (key: string, defaultValue?: string) => string | undefined;
	getSettingAsNumber: (key: string, defaultValue?: number) => number | undefined;
	getSettingAsBoolean: (key: string, defaultValue?: boolean) => boolean;
	getSettingAsNumberArray: (key: string) => number[];
	getSettingAsStringArray: (key: string) => string[];

	token: string | null;
	setToken: (token: string | null) => void;

	currentUser: string;
	setCurrentUser: (user: string) => void;

	currentUserId: number;
	setCurrentUserId: (id: number) => void;

	currentUserAddressSystems: AddressSystem[];
	setCurrentUserAddressSystems: (addressSystems: AddressSystem[]) => void;

	selectedAddressSystem: AddressSystem;
	setSelectedAddressSystem: (id: AddressSystem) => void;

	selectedAddressSystemStatuses: GeometryStatus[];
	setSelectedAddressSystemStatuses: (statuses: GeometryStatus[]) => void;

	selectedAddressSystemQualityControls: GeometryStatus[];
	setSelectedAddressSystemQualityControls: (qualityControls: GeometryStatus[]) => void;

	organisationId: number;
	setOrganisationId: (organisationId: number) => void;

	loadingOverlayVisible: boolean;
	setLoadingOverlayVisible: (visible: boolean) => void;

	isUserRegistered: boolean | null; // null = unknown (loading), true/false = resolved
	setIsUserRegistered: (isUserRegistered: boolean | null) => void;

	currentUserEmail: string;
	setCurrentUserEmail: (user: string) => void;

	currentUsername: string;
	setCurrentUsername: (username: string) => void;

	currentUserPhotoUrl: string | null;
	setCurrentUserPhotoUrl: (photoUrl: string | null) => void;

	// Drawing layer clearing coordination
	shouldClearDrawing: boolean;
	excludeInstanceId: string | null;
	triggerClearDrawing: (excludeInstanceId?: string) => void;
	resetClearDrawing: () => void;
	
	// Modal minimized state
	isModalMinimized: boolean;
	setModalMinimized: (value: boolean) => void;
	
	// Work items refresh trigger for cross-tab synchronization
	workItemsRefreshTrigger: number;
	triggerWorkItemsRefresh: () => void;
	
	currentUserRole: number;
	setCurrentUserRole: (role: number) => void;

	allUsers: UserType[];
	setAllUsers: (userData: UserType[]) => void;

	allOrganisations: OrganisationType[];
	setAllOrganisations: (userData: OrganisationType[]) => void;

	allAddressSystems: ApplicationAddressSystemType[];
	setAllAddressSystems: (userData: ApplicationAddressSystemType[]) => void;

	allRoles: Role[];
	setAllRoles: (roleData: Role[]) => void;

	// Map related 

	currentLocation: Location | null;
	setCurrentLocation: (location: Location) => void;

	map: OLMap | null;
	setMap: (map: OLMap) => void;

	//This is the data that will be used to populate the location tree
	locationTreeData: TreeNodeData[];
	setLocationTreeData: (data: TreeNodeData[]) => void;

	// Original unfiltered tree data for search operations
	originalLocationTreeData: TreeNodeData[];
	setOriginalLocationTreeData: (data: TreeNodeData[]) => void;

	drawing: boolean;
	setDrawing: (drawing: boolean) => void;

	drawingMode: DrawingModes; // 'polygon' | 'multiline' | 'point'
	setDrawingMode: (mode: DrawingModes) => void;

	locations: Location[];
	setLocations: (locations: Location[]) => void;

	drawnPolygons: Polygon[];
	setDrawnPolygons: (polygons: Polygon[]) => void;

	currentPolylines: RIMSPolyline[];
	setCurrentPolylines: (polylines: RIMSPolyline[]) => void;

	currentPolygons: Polygon[];
	setCurrentPolygons: (polygons: Polygon[]) => void;

	currentPolypoints: Point[]; // Define the type based on your application needs
	setCurrentPolypoints: (polypoints: Point[]) => void;

	// Search functionality
	searchFilter: string;
	setSearchFilter: (searchTerm: string) => void;

	// Layer visibility toggles
	userGeometriesVisible: boolean;
	setUserGeometriesVisible: (visible: boolean) => void;

	layerToggles: { [layerName: string]: boolean };
	setLayerToggles: (toggles: { [layerName: string]: boolean }) => void;

	// Contract search filters
	contractSearchActive: boolean;
	setContractSearchActive: (active: boolean) => void;
	filteredContractIds: number[];
	setFilteredContractIds: (ids: number[]) => void;

	// Geometry search filters and results
	geometrySearchFilters: GeometrySearchFilters;
	setGeometrySearchFilters: (filters: GeometrySearchFilters) => void;
	geometrySearchResults: GeometrySearchResult[];
	setGeometrySearchResults: (results: GeometrySearchResult[]) => void;
	geometrySearchLoading: boolean;
	setGeometrySearchLoading: (loading: boolean) => void;

	// Selected geometry for ObjectForm (shared between SearchMenu and mapPanel)
	selectedGeometry: BaseGeometry | null;
	setSelectedGeometry: (geometry: BaseGeometry | null) => void;

	// Session reset (logout)
	resetSession: () => void;

	// Update geometries with API response data
	updateGeometries: (polylines: RIMSPolyline[], polygons: Polygon[], polypoints: Point[]) => void;

	// Update search results with API response data
	updateSearchResults: (results: GeometrySearchResult[], polylines: RIMSPolyline[], polygons: Polygon[], polypoints: Point[]) => void;
};


const useTransientStore = create<TransientStoreType>()(
	persist(
		(set, get) => ({

			token: null,
			setToken: (token) => set({ token: token }),

			currentUser: '',
			setCurrentUser: (user) => set({ currentUser: user }),

			currentUserId: 0,
			setCurrentUserId: (id) => set({ currentUserId: id }),

			currentUserAddressSystems: [],
			setCurrentUserAddressSystems: (addressSystems) => set({ currentUserAddressSystems: addressSystems }),

			selectedAddressSystem: { name: '', id: 0, status: 0 },
			setSelectedAddressSystem: (id) => set({ selectedAddressSystem: id }),

			selectedAddressSystemStatuses: [],
			setSelectedAddressSystemStatuses: (statuses) => set({ selectedAddressSystemStatuses: statuses }),

			selectedAddressSystemQualityControls: [],
			setSelectedAddressSystemQualityControls: (qualityControls) => set({ selectedAddressSystemQualityControls: qualityControls }),

			organisationId: 0,
			setOrganisationId: (organisationId) => set({ organisationId: organisationId }),

			loadingOverlayVisible: false,
			setLoadingOverlayVisible: (visible) => set({ loadingOverlayVisible: visible }),

			isUserRegistered: null,
			setIsUserRegistered: (isRegistered) => set({ isUserRegistered: isRegistered }),

			currentUserEmail: '',
			setCurrentUserEmail: (currentEmail) => set({ currentUserEmail: currentEmail }),

			currentUsername: '',
			setCurrentUsername: (username) => set({ currentUsername: username }),

			currentUserPhotoUrl: null,
			setCurrentUserPhotoUrl: (photoUrl) => set({ currentUserPhotoUrl: photoUrl }),

			currentUserRole: 0,
			setCurrentUserRole: (userRole) => set({ currentUserRole: userRole }),

			allUsers: [],
			setAllUsers: (userData) => set({ allUsers: userData }),

			allOrganisations: [],
			setAllOrganisations: (organisationData) => set({ allOrganisations: organisationData }),

			allAddressSystems: [],
			setAllAddressSystems: (addressSystems) => set({ allAddressSystems: addressSystems }),

			allRoles: [],
			setAllRoles: (roleData) => set({ allRoles: roleData }),

			// Client system settings
			clientSystemSettings: new Map<string, string>(),
			setClientSystemSettings: (settings) => set({ clientSystemSettings: settings }),

			// Helper to get raw string value
			getSettingValue: (key: string, defaultValue?: string) => {
				const settings = get().clientSystemSettings;
				return settings.get(key) ?? defaultValue;
			},

			// Helper to parse as number
			getSettingAsNumber: (key: string, defaultValue?: number) => {
				const value = get().getSettingValue(key);
				if (!value) return defaultValue;
				const parsed = Number(value);
				return isNaN(parsed) ? defaultValue : parsed;
			},

			// Helper to parse as boolean
			getSettingAsBoolean: (key: string, defaultValue: boolean = false) => {
				const value = get().getSettingValue(key);
				if (!value) return defaultValue;
				const lower = value.toLowerCase().trim();
				return lower === 'true' || lower === '1' || lower === 'yes';
			},

			// Helper to parse comma separated numbers
			getSettingAsNumberArray: (key: string) => {
				const value = get().getSettingValue(key);
				if (!value) return [];
				return value
					.split(',')
					.map(s => s.trim())
					.filter(s => s.length > 0)
					.map(s => Number(s))
					.filter(n => !isNaN(n));
			},

			// Helper to parse comma separated strings
			getSettingAsStringArray: (key: string) => {
				const value = get().getSettingValue(key);
				if (!value) return [];
				return value
					.split(',')
					.map(s => s.trim())
					.filter(s => s.length > 0);
			},

			// Map related

			currentLocation: JSON.parse(localStorage.getItem('currentLocation') as string) || null,
			setCurrentLocation: (location) => {
				localStorage.setItem('currentLocation', JSON.stringify(location));
				set({ currentLocation: location });
			},

			map: null,
			setMap: (map) => set({ map: map }),

			locationTreeData: [],
			setLocationTreeData: (data) => set({ locationTreeData: data }),

			// Original unfiltered tree data for search operations
			originalLocationTreeData: [],
			setOriginalLocationTreeData: (data) => set({ originalLocationTreeData: data }),

			drawing: false,
			setDrawing: (drawing) => set({ drawing: drawing }),

			drawingMode: DrawingModes.Polygon,
			setDrawingMode: (mode) => set({ drawingMode: mode }),

			locations: [],
			setLocations: (newLocations) => set({ locations: newLocations }),

			drawnPolygons: [],
			setDrawnPolygons: (polygons) => set({ drawnPolygons: polygons }),

			currentPolylines: [],
			setCurrentPolylines: (polylines) => set({ currentPolylines: polylines }),

			currentPolygons: [],
			setCurrentPolygons: (polygons) => set({ currentPolygons: polygons }),

			currentPolypoints: [],
			setCurrentPolypoints: (polypoints) => set({ currentPolypoints: polypoints }),

			// Search functionality
			searchFilter: '',
			setSearchFilter: (searchTerm) => set({ searchFilter: searchTerm }),

			// Layer visibility toggles
			userGeometriesVisible: true,
			setUserGeometriesVisible: (visible) => set({ userGeometriesVisible: visible }),

			layerToggles: {},
			setLayerToggles: (toggles) => set({ layerToggles: toggles }),

			// Contract search filters
			contractSearchActive: false,
			setContractSearchActive: (active) => set({ contractSearchActive: active }),
			filteredContractIds: [],
			setFilteredContractIds: (ids) => set({ filteredContractIds: ids }),

			// Geometry search filters and results
			geometrySearchFilters: {
				contractStatus: 'active',
				selectedContractIds: [],
				geometryName: '',
				geometryType: 'all',
				createdBy: '',
				foremanId: null,
				qualityControl1Id: null,
				qualityControl2Id: null,
				fromDate: null,
				toDate: null,
				geometryStatusId: null,
				sortBy: 'name'
			},
			setGeometrySearchFilters: (filters) => set({ geometrySearchFilters: filters }),
			geometrySearchResults: [],
			setGeometrySearchResults: (results) => set({ geometrySearchResults: results }),
			geometrySearchLoading: false,
			setGeometrySearchLoading: (loading) => set({ geometrySearchLoading: loading }),

			// Selected geometry for ObjectForm
			selectedGeometry: null,
			setSelectedGeometry: (geometry) => set({ selectedGeometry: geometry }),

			// Session reset helper to clear user-specific persisted data
			resetSession: () => set({
				currentUser: '',
				currentUsername: '',
				currentUserPhotoUrl: null,
				currentUserId: 0,
				currentUserEmail: '',
				currentUserRole: 0,
				selectedAddressSystem: { name: '', id: 0 },
				currentUserAddressSystems: [],
				selectedAddressSystemStatuses: [],
				selectedAddressSystemQualityControls: [],
				clientSystemSettings: new Map<string, string>(),
				organisationId: 0,
				loadingOverlayVisible: false,
				allUsers: [],
				allOrganisations: [],
				allAddressSystems: [],
				allRoles: [],
				currentLocation: null,
				map: null,
				locationTreeData: [],
				originalLocationTreeData: [],
				drawing: false,
				drawingMode: DrawingModes.Polygon,
				locations: [],
				drawnPolygons: [],
				currentPolylines: [],
				currentPolygons: [],
				currentPolypoints: [],
				searchFilter: '',
				userGeometriesVisible: true,
				layerToggles: {},
				contractSearchActive: false,
				filteredContractIds: [],
				isUserRegistered: null,
				geometrySearchFilters: {
					contractStatus: 'active',
					selectedContractIds: [],
					geometryName: '',
					geometryType: 'all',
					createdBy: '',
					foremanId: null,
					qualityControl1Id: null,
					qualityControl2Id: null,
					fromDate: null,
					toDate: null,
					geometryStatusId: null,
					sortBy: 'name'
				},
				geometrySearchResults: [],
				geometrySearchLoading: false
			}),

			// Update geometries with data from component's API call
			updateGeometries: (polylines, polygons, polypoints) => set({
				currentPolylines: polylines,
				currentPolygons: polygons,
				currentPolypoints: polypoints
			}),

			// Update search results with data from component's API call
			updateSearchResults: (results, polylines, polygons, polypoints) => set({
				geometrySearchResults: results,
				currentPolylines: polylines,
				currentPolygons: polygons,
				currentPolypoints: polypoints
			}),

			// Drawing layer clearing
			shouldClearDrawing: false,
			excludeInstanceId: null,
			triggerClearDrawing: (excludeInstanceId?: string) => set({ shouldClearDrawing: true, excludeInstanceId: excludeInstanceId || null }),
			resetClearDrawing: () => set({ shouldClearDrawing: false, excludeInstanceId: null }),
			
			// Modal minimized state
			isModalMinimized: false,
			setModalMinimized: (value: boolean) => set({ isModalMinimized: value }),
			
			// Work items refresh trigger
			workItemsRefreshTrigger: 0,
			triggerWorkItemsRefresh: () => set((state) => ({ workItemsRefreshTrigger: state.workItemsRefreshTrigger + 1 }))

		}),
		{
			name: 'transient-store', // storage key
			version: 5, // Increment this version on each deployment to clear translation cache
			partialize: (state) => ({
				currentUser: state.currentUser,
				currentUsername: state.currentUsername,
				currentUserPhotoUrl: state.currentUserPhotoUrl,
				currentUserId: state.currentUserId,
				currentUserEmail: state.currentUserEmail,
				currentUserRole: state.currentUserRole,
				selectedAddressSystem: state.selectedAddressSystem,
				currentUserAddressSystems: state.currentUserAddressSystems
			}),
			// (Optional) migrate older persisted shapes to new shape
			migrate: (persistedState: any, version) => {
				if (!persistedState) return persistedState;
				if (version < 2) {
					return {
						...persistedState,
						selectedAddressSystem: persistedState.selectedAddressSystem || { name: '', id: 0, status: 0 },
						currentUserAddressSystems: persistedState.currentUserAddressSystems || []
					};
				}
				if (version < 3) {
					// Clear any previously cached settings for security
					return {
						...persistedState,
						clientSystemSettings: {}
					};
				}
				if (version < 4) {
					// Add currentUserRole persistence for admin tab visibility
					return {
						...persistedState,
						currentUserRole: persistedState.currentUserRole || 0
					};
				}
				if (version < 5) {
					// Clear ALL persisted data on new deployment
					console.log('[transcientStore] New version detected - clearing all persisted data');
					try {
						// Clear selectedLanguage from languageStore
						localStorage.removeItem('selectedLanguage');
						// Clear currentLocation
						localStorage.removeItem('currentLocation');
					} catch (err) {
						console.error('[transcientStore] Failed to clear localStorage:', err);
					}
					// Return empty persisted state (forces re-login/re-selection)
					return {
						currentUser: '',
						currentUserId: 0,
						currentUserEmail: '',
						currentUserRole: 0,
						selectedAddressSystem: { name: '', id: 0, status: 0 },
						currentUserAddressSystems: []
					};
				}
				return persistedState;
			}
		}
	)
);

export default useTransientStore;
